import React from 'react';
import ResumeEmptyShell from './ResumeEmptyShell';
// Legacy templates (kept for backward compat)
import MbaTemplate                  from '../templates/MbaTemplate';
import TechTemplate                 from '../templates/TechTemplate';
import TimelineTemplate             from '../templates/TimelineTemplate';
import FinProTemplate               from '../templates/FinProTemplate';

// ── New ATS Verified Templates ──
import HarvardATSClassicTemplate    from '../templates/HarvardATSClassicTemplate';
import ModernATSProTemplate         from '../templates/ModernATSProTemplate';
import FAANGEngineeringTemplate     from '../templates/FAANGEngineeringTemplate';
import ExecutiveProfessionalTemplate from '../templates/ExecutiveProfessionalTemplate';
import CleanMinimalATSTemplate      from '../templates/CleanMinimalATSTemplate';
import TwoColumnATSTemplate         from '../templates/TwoColumnATSTemplate';

// ── New Student Templates ──
import CampusPlacementTemplate      from '../templates/CampusPlacementTemplate';
import InternshipHunterTemplate     from '../templates/InternshipHunterTemplate';
import FresherPlusTemplate          from '../templates/FresherPlusTemplate';

// ── New Professional Templates ──
import FinanceProTemplate           from '../templates/FinanceProTemplate';
import ProductConsultingTemplate    from '../templates/ProductConsultingTemplate';
import CreativeProfessionalTemplate from '../templates/CreativeProfessionalTemplate';

// ── Decoupled rendering platform imports ──
import { templates, getTemplate } from '../templates/registry';
import { ResumeRenderer } from '../core/renderer/ResumeRenderer';
import { normalizeTemplateId } from '../utils/normalizeResumeData';

const legacyTemplateMap = {
  // Legacy IDs (backward compatible)
  mba:                    MbaTemplate,
  tech:                   TechTemplate,
  timeline:               TimelineTemplate,
  finpro:                 FinProTemplate,

  // ATS Verified
  harvard_classic:        HarvardATSClassicTemplate,
  modern_ats_pro:         ModernATSProTemplate,
  faang_engineering:      FAANGEngineeringTemplate,
  executive_professional: ExecutiveProfessionalTemplate,
  clean_minimal_ats:      CleanMinimalATSTemplate,
  two_column_ats:         TwoColumnATSTemplate,

  // Student
  campus_placement:       CampusPlacementTemplate,
  internship_hunter:      InternshipHunterTemplate,
  fresher_plus:           FresherPlusTemplate,

  // Professional
  finance_pro:            FinanceProTemplate,
  product_consulting:     ProductConsultingTemplate,
  creative_professional:  CreativeProfessionalTemplate,
};

function ResumePreview({ 
  resumeData, 
  template, 
  accentColor, 
  fontFamily,
  fontScale = 1, 
  editMode = false, 
  showPageBoundaries = false,
  onFieldUpdate, 
  onDeleteBullet, 
  onDeleteSkill 
}) {
  // Normalize template to canonical kebab-case AND resolve legacy aliases (e.g. 'mba' -> 'modern-ats-pro').
  // IMPORTANT: this must use the exact same resolver as the PDF/DOCX export path (PrintPage.jsx uses
  // this same normalizeTemplateId from utils/normalizeResumeData). Previously this component only
  // did a naive underscore->hyphen replace with no alias table, so a document stored with a legacy
  // template id (e.g. 'mba', 'tech', 'timeline', 'finpro') would render here via the OLD standalone
  // template components (which categorize skills via templateHelpers.categorizeSkills, different
  // labels) while the export path silently resolved the same id to a totally different modern
  // template (via ResumeRenderer + resumeIntelligence.autoCategorizeSkills, different labels again).
  // That produced skills grouped one way in the editor's live preview and a different way (or not
  // grouped at all) in the downloaded PDF. Using the shared resolver keeps preview and export in sync.
  const normalizedTemplateId = normalizeTemplateId(template);
  const hasNewTemplate = !!templates[normalizedTemplateId];

  if (process.env.NODE_ENV === 'development') {
    console.log(`[PIPE:template S5:ResumePreview] prop.template=${template} → normalized=${normalizedTemplateId} hasNewTemplate=${hasNewTemplate}`);
  }

  // ── Empty state guard (Sprint 1.2.2 — renderer owns empty state) ──
  // React passes null when a new blank document is active.
  // The renderer draws the professional empty-state shell; no fake data in React.
  const isEmpty = !resumeData || (
    !resumeData.summary &&
    !(resumeData.experience && resumeData.experience.length > 0) &&
    !(resumeData.education && resumeData.education.length > 0) &&
    !(resumeData.skills && resumeData.skills.length > 0) &&
    !resumeData.name
  );

  if (isEmpty) {
    return <ResumeEmptyShell accentColor={accentColor} fontScale={fontScale} />;
  }

  if (hasNewTemplate) {
    const templateConfig = getTemplate(normalizedTemplateId);
    
    // Inject user overrides (e.g. customized color and spacing scale sliders)
    const userOverrides = {};
    if (accentColor) {
      userOverrides['--color-primary'] = accentColor;
    }
    if (fontScale !== 1) {
      userOverrides['--spacing-multiplier'] = fontScale;
      userOverrides['--font-size-display'] = `${24 * fontScale}pt`;
      userOverrides['--font-size-heading'] = `${11 * fontScale}pt`;
      userOverrides['--font-size-subheading'] = `${9.5 * fontScale}pt`;
      userOverrides['--font-size-body'] = `${9 * fontScale}pt`;
      userOverrides['--font-size-bullet'] = `${8.8 * fontScale}pt`;
      userOverrides['--font-size-meta'] = `${8 * fontScale}pt`;
      userOverrides['--font-size-caption'] = `${7.5 * fontScale}pt`;
      userOverrides['--font-size-label'] = `${8 * fontScale}pt`;
    }
    if (fontFamily) {
      userOverrides['--font-family-primary'] = fontFamily;
      userOverrides['--font-family-secondary'] = fontFamily;
    }

    return (
      <ResumeRenderer
        resumeData={resumeData}
        templateConfig={templateConfig}
        themeId={templateConfig.defaultTheme}
        layoutId={templateConfig.defaultLayout}
        personaId="engineering" // Defaults to engineering for standard profiles
        userOverrides={userOverrides}
        fontScale={fontScale}
        targetPageCount={1}
        showPageBoundaries={showPageBoundaries}
      />
    );
  }

  // Fallback to legacy layout components (only for true pre-2025 IDs: mba, tech, timeline, finpro).
  // These 4 IDs are NOT in the gallery and are only kept for old documents.
  const Component = legacyTemplateMap[normalizedTemplateId] || legacyTemplateMap[template];
  if (!Component) {
    // A gallery template ID reached here without being in the registry.
    // This is a registry gap — surface it immediately instead of rendering Harvard.
    throw new Error(
      `[ResumePreview] Template "${normalizedTemplateId}" is not registered in the frontend ` +
      `template registry and has no legacy component. ` +
      `Add it to src/templates/registry.js or check the template ID normalization.`
    );
  }

  return (
    <div style={fontFamily ? { fontFamily: `'${fontFamily}', sans-serif` } : null}>
      <Component
        resumeData={resumeData}
        accentColor={accentColor}
        fontScale={fontScale}
        editMode={editMode}
        onFieldUpdate={onFieldUpdate || (() => {})}
        onDeleteBullet={onDeleteBullet || null}
        onDeleteSkill={onDeleteSkill || null}
      />
    </div>
  );
}

export default ResumePreview;